import { useCallback, useEffect, useState } from 'react'

import { DayV2, fetchV2DaysForUser } from '@library/api-deliveries'

type Props = {
  slotId: string
  cutOffDateTimeFrom: string
  cutOffDateTimeUntil: string
  postcode: string
  selectedDate?: string
}

export type DeliveryDays = {
  data: DayV2[] | null
  deliveryDate: string | null
  dsltId: string | null
}

/**
 * Loading, Error State, Delivery Day
 */
type UseSubscriptionToDeliveryDays = [boolean, Error | null, DeliveryDays]

export function useSubscriptionToDeliveryDays({
  slotId,
  cutOffDateTimeFrom,
  cutOffDateTimeUntil,
  postcode,
  selectedDate,
}: Props): UseSubscriptionToDeliveryDays {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [returnedDeliveryDay, setDeliveryDay] = useState<DeliveryDays>({
    data: null,
    deliveryDate: null,
    dsltId: null,
  })

  const tryFetchDays = useCallback(async () => {
    try {
      const data = await fetchV2DaysForUser({
        filters: {
          cutoff_datetime_from: cutOffDateTimeFrom,
          cutoff_datetime_until: cutOffDateTimeUntil,
        },
        sort: 'date',
        direction: 'asc',
        postcode,
        order_type: 'resubscription',
      })

      const defaultDeliveryDays = data.map((day) => ({
        ...day,
        daySlotLeadTimes: [day.daySlotLeadTimes[0]],
      }))

      const deliverySlotMatchedWithSubscriptionSlotId = defaultDeliveryDays
        .flatMap((day) => day.daySlotLeadTimes)
        .find((dslt) => {
          // slotId is not unique to one DSLT, it is shared among multiple DSLTs
          // in the case of a date is not selected, we will default to the nearest delivery date
          // otherwise, we will match the slotId and date
          if (!selectedDate) {
            return dslt.slotId === slotId
          }

          return dslt.slotId === slotId && dslt.date === selectedDate
        })

      setDeliveryDay({
        data: defaultDeliveryDays,
        deliveryDate: deliverySlotMatchedWithSubscriptionSlotId?.date ?? null,
        dsltId: deliverySlotMatchedWithSubscriptionSlotId?.id ?? null,
      })
    } catch (err: unknown) {
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [cutOffDateTimeFrom, cutOffDateTimeUntil, postcode, selectedDate, slotId])

  useEffect(() => {
    tryFetchDays()
  }, [tryFetchDays])

  return [loading, error, returnedDeliveryDay]
}
